import React, { useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Button, Icons, Row } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import { PROCEDURE_TYPE, USER_TYPE, USER_TYPE_LIST } from '../../../configs';
import FieldDTOWithCol from '../../../components/fields/Abstract/FieldDTOWithCol';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import {
  createCarrier,
  editCarrierById,
  loadCarrierFieldsValidation, szltHelper,
  vehiclesHelper,
} from '../actions';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import {
  getResponseError, responseLogWithNotification, setFormError,
} from '../../../utils/utils';
import CarrierDTO from '../../../dto/CarrierDTO';
import DatePicker from '../../../components/fields/DatePicker/DatePicker';
import Switches from '../../../components/fields/Switches/Switches';
import ApplicantForm from '../../Application/components/FormBlocks/ApplicantForm';
import { showGlobalConfirm } from '../../System/actions';
import {
  renderRegistryCarrierItemMin,
  renderRegistryValidationMessage,
} from '../../../utils/formatters';
import RegistryNumberSelectorById from '../../../components/fields/SelectInput/RegistryNumberSelectorById';
import OsgopInsuranceForm from '../../Application/components/FormBlocks/OsgopInsuranceForm';

export default function CarrierForm(props) {
  const {
    onClose = () => {},
    loadData = () => {},
    data = {},
    isEdit = false,
    formMeta,
  } = props;
  const dispatch = useDispatch();
  const [applicantType, setApplicantType] = useState(data && data.legal_entity_type ? data.legal_entity_type : USER_TYPE.LEGAL);
  const [hasEmployees, setHasEmployees] = useState(!!data.has_employees);
  const { vehicles = [] } = data;
  const {
    watch,
    control,
    getValues,
    setValue,
    formState: { errors },
    setError,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      legal_entity_type: applicantType,
      ...data,
      vehicle_ids: vehicles.map(i => i.id),
    },
  });

  const {
    fields: osgopFields, append: appendOsgop, remove: removeOsgop,
  } = useFieldArray({ control, name: 'osgop_insurances' });

  React.useEffect(() => {
    const subscription = watch(values => {
      setApplicantType(values.legal_entity_type);
      setHasEmployees(values.has_employees);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleValidateAndSave = async () => {
    if (!data?.id) {
      return handleSave();
    }
    const values = getValues();
    dispatch(loadCarrierFieldsValidation({ id: data.id, data: { input_data: values } })).then(data => {
      const checkErrors = data?.checks || {};
      const errors = data?.errors || {};
      const checkErrorsKeys = Object.keys(checkErrors);
      const errorsKeys = Object.keys(errors);
      if (Object.values(checkErrors).filter(f => f.status !== 2).length + errorsKeys.length) {
        dispatch(showGlobalConfirm({
          confirmTitle: 'Реестровая запись содержит ошибки в полях',
          confirmMessage: renderRegistryValidationMessage(errors, checkErrors, checkErrorsKeys, errorsKeys),
          confirmButtonText: 'Сохранить',
          confirmClassName: 'remove-preset',
        })).then(() => {
          handleSave();
        }).catch(() => {});
      } else {
        handleSave();
      }
    }).catch(responseLogWithNotification);
  };

  const handleSave = async () => {
    const keys = Object.keys(errors);
    if (errors && keys.length) {
      errorNotification('Сохранение формы перевозчика', 'Ошибка заполнения формы');
      const { ref } = errors[keys[0]];
      ref.focus();
      setError('number', { type: 'custom', message: 'custom message' });
      return null;
    }
    const action = isEdit ? editCarrierById : createCarrier;
    const values = getValues();
    dispatch(action(values))
      .then(() => {
        loadData();
        onClose();
        successNotification('Сохранение формы перевозчика', 'Сформировано Решение о внесении изменений');
      })
      .catch(response => {
        setFormError(response, setError);
        errorNotification('Сохранение формы перевозчика', getResponseError({ error: response && response.message ? response.message : '' }));
      });
  };
  const DTO = new CarrierDTO({ legal_entity_type: applicantType });
  const attributes = DTO.getAttributesByName();

  return ReactDOM.createPortal(
    <div className="application-form">
      <div className="application-form-header">
        <div className="breadcrumbs">
          <span className="breadcrumbs-prev">Реестры</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-prev">Реестр перевозчиков</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-prev">Разрешение</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-current">Разрешение перевозчика № {data?.number || 'Без номера'} ({isEdit ? 'редактирование' : 'создание'})</span>
        </div>
        <div className="close-button" onClick={onClose}>
          <Icons width={24} height={24} customIcon icon="close" />
        </div>
      </div>
      <div className="application-form-body">
        <form className="application-form-content small" onSubmit={handleSubmit(handleValidateAndSave)}>
          <div className="application-form-fields-header">Заполните информацию о документе</div>
          <div className="application-form-fields">
            <div className="application-form-fields-wrapper custom-scroll-theme">
              <Row className="row-cols-3">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.number}
                  variant="material"
                  disabled
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={DatePicker}
                  dtoObject={attributes.effective_date}
                  changeFormat="YYYY-MM-DD"
                  withLabel
                  variant="material"
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={DatePicker}
                  changeFormat="YYYY-MM-DD"
                  dtoObject={attributes.expiration_date}
                  withLabel
                  variant="material"
                />
              </Row>
              <div className="f4 mt-3">Перевозчик</div>
              <ApplicantForm
                watch={watch}
                getValues={getValues}
                control={control}
                setValue={setValue}
                formMeta={formMeta}
                availableTypes={USER_TYPE_LIST.filter(f => f.value !== USER_TYPE.PRIVATE_PERSON_OLD)}
                applicantType={applicantType}
                hideFileInput
              />
              {/* дублируется из блока выше */}
              {/* <Row className="row-cols-2"> */}
              {/*  <FieldDTOWithCol */}
              {/*    control={control} */}
              {/*    formMeta={formMeta} */}
              {/*    type="text" */}
              {/*    component={TextInputWithLabel} */}
              {/*    dtoObject={attributes.commercial_name} */}
              {/*    variant="material" */}
              {/*    transform={quotesTransform} */}
              {/*  /> */}
              {/*  <FieldDTOWithCol */}
              {/*    control={control} */}
              {/*    formMeta={formMeta} */}
              {/*    type="text" */}
              {/*    component={TextInputWithLabel} */}
              {/*    dtoObject={attributes.logo_files} */}
              {/*    transform={quotesTransform} */}
              {/*    variant="material" */}
              {/*  /> */}
              {/* </Row> */}
              <div className="f4 mt-3">Информация о водителе</div>
              {applicantType === USER_TYPE.PRIVATE_PERSON || applicantType === USER_TYPE.INDIVIDUAL_ENTREPRENEUR ? (
                <>
                  {applicantType === USER_TYPE.INDIVIDUAL_ENTREPRENEUR ? (
                    <Row className="row-cols-1">
                      <FieldDTOWithCol
                        control={control}
                        formMeta={formMeta}
                        type="text"
                        component={Switches}
                        dtoObject={attributes.has_employees}
                        variant="material"
                      />
                    </Row>
                  ) : null}
                  <Row className="row-cols-2">
                    <FieldDTOWithCol
                      control={control}
                      formMeta={formMeta}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.driver_license_series}
                      variant="material"
                      disabled={hasEmployees}
                    />
                    <FieldDTOWithCol
                      control={control}
                      formMeta={formMeta}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.driver_license_number}
                      variant="material"
                      disabled={hasEmployees}
                    />
                  </Row>
                  <Row className="row-cols-2">
                    <FieldDTOWithCol
                      control={control}
                      formMeta={formMeta}
                      type="text"
                      component={DatePicker}
                      changeFormat="YYYY-MM-DD"
                      withLabel
                      dtoObject={attributes.driver_license_issue_date}
                      variant="material"
                      disabled={hasEmployees}
                    />
                    <FieldDTOWithCol
                      control={control}
                      formMeta={formMeta}
                      type="text"
                      component={DatePicker}
                      changeFormat="YYYY-MM-DD"
                      dtoObject={attributes.driver_license_expiration_date}
                      withLabel
                      variant="material"
                      disabled={hasEmployees}
                    />
                  </Row>
                  <Row className="row-cols-1">
                    <FieldDTOWithCol
                      control={control}
                      formMeta={formMeta}
                      type="text"
                      component={TextInputWithLabel}
                      changeFormat="YYYY-MM-DD"
                      dtoObject={attributes.medical_examination_address}
                      variant="material"
                      disabled={hasEmployees && applicantType !== USER_TYPE.INDIVIDUAL_ENTREPRENEUR}
                    />
                  </Row>
                </>
              )
                : (
                  <Row className="row-cols-1">
                    <FieldDTOWithCol
                      control={control}
                      formMeta={formMeta}
                      type="text"
                      component={TextInputWithLabel}
                      changeFormat="YYYY-MM-DD"
                      dtoObject={attributes.medical_examination_address}
                      variant="material"
                    />
                  </Row>
                )}
              <div className="f4 mt-3">Дополнительная информация</div>
              <Row className="row-cols-2">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.decision}
                  variant="material"
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={DatePicker}
                  changeFormat="YYYY-MM-DD"
                  dtoObject={attributes.decision_date}
                  variant="material"
                  withLabel
                />
              </Row>
              {applicantType === USER_TYPE.PRIVATE_PERSON ? (
                <>
                  <Row className="row-cols-2">
                    <FieldDTOWithCol
                      control={control}
                      formMeta={formMeta}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.carriage_contract_number}
                      variant="material"
                    />
                    <FieldDTOWithCol
                      control={control}
                      formMeta={formMeta}
                      type="text"
                      component={DatePicker}
                      changeFormat="YYYY-MM-DD"
                      dtoObject={attributes.carriage_contract_effective_date}
                      variant="material"
                      withLabel
                    />
                  </Row>
                  <Row className="row-cols-1">
                    <FieldDTOWithCol
                      control={control}
                      formMeta={formMeta}
                      type="text"
                      component={DatePicker}
                      changeFormat="YYYY-MM-DD"
                      dtoObject={attributes.carriage_contract_expiration_date}
                      variant="material"
                      withLabel
                    />
                  </Row>
                </>
              ) : null}

              <div className="f4 mt-3">Информация по ОСГОП перевозчика</div>
              {osgopFields.map((field, index) => (
                <div className="carrier-szlt-registry mt-3" key={index}>
                  <OsgopInsuranceForm
                    control={control}
                    formMeta={formMeta}
                    selectedProcedure={PROCEDURE_TYPE.PROCEDURE_1227_1}
                    labelPrefix="osgop_"
                    prefix="osgop_insurances."
                    index={index}
                    hideFileInput
                  />
                  <Button variant="danger" className="mt-3" onClick={() => removeOsgop(index)}>Удалить запись</Button>
                </div>
              ))}
              <Button className="mt-3" onClick={() => appendOsgop({})}>Добавить запись</Button>

              <div className="f4 mt-3">Реестр СЗЛТ</div>
              <Row className="row-cols-1">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={RegistryNumberSelectorById}
                  dtoObject={attributes.order_service_id}
                  variant="material"
                  withLabel
                  loadAction={szltHelper}
                  setOgrnValue={ogrn => setValue('order_service_ogrn', ogrn)}
                  label="Номер разрешения в реестре СЗЛТ"
                />
              </Row>
              <Row className="row-cols-2">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.order_service_ogrn}
                  variant="material"
                  withLabel
                  readOnly
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={TextInputWithLabel}
                  dtoObject={attributes.contract_number}
                  variant="material"
                  withLabel
                />
              </Row>
              <Row className="row-cols-2">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={DatePicker}
                  changeFormat="YYYY-MM-DD"
                  dtoObject={attributes.contract_effective_date}
                  variant="material"
                  withLabel
                />
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={DatePicker}
                  changeFormat="YYYY-MM-DD"
                  dtoObject={attributes.contract_expiration_date}
                  variant="material"
                  withLabel
                />
              </Row>
              <div className="f4 mt-3">Реестр ЛТ</div>
              <Row className="row-cols-1">
                <FieldDTOWithCol
                  control={control}
                  formMeta={formMeta}
                  type="text"
                  component={RegistryNumberSelectorById}
                  dtoObject={attributes.vehicle_ids}
                  customRenderOptions={(item, onChange) => renderRegistryCarrierItemMin(item, data.id, onChange, !isEdit)}
                  variant="material"
                  withLabel
                  loadAction={vehiclesHelper}
                  // filter={{ carrier_id: data.id }}
                  multiple
                />
              </Row>
            </div>
          </div>
          <div className="application-form-footer">
            <Button variant="secondary" onClick={() => onClose()}>Отменить</Button>
            <Button type="submit">{isEdit ? 'Сохранить' : 'Создать'}</Button>
          </div>
        </form>
      </div>
    </div>,
    document.getElementById('app-content'),
  );
}
